<template>
  <aside class="filters">
    <header class="filters-header">
      <h2 class="filters-title">{{t('Filters')}}</h2>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="filters-clear"
        @click="onClearFilters"
      >
        {{ t('Clear') }}
      </button>
    </header>

    <UIAccordion
      :items="filterItems"
      :multiple="true"
      :default-open="['pricing', 'rating', 'amenities']"
      class="filter-variant"
      item-key="id"
    >
      <template #header="{ item, isOpen }">
        <span class="filters-section__title">{{ typeof item === 'string' ? item : item.title }}</span>
        <span class="icon icon-chevron-down filters-section__icon" :class="{ 'is-open': isOpen }"></span>
      </template>

      <template #content-pricing>
        <Pricing @update="updatePriceFilter"/>
      </template>

      <template #content-rating>
        <Rating @update="updateRatingFilter"/>
      </template>

      <template #content-amenities>
        <Amenities @update="updateAmenitiesFilter"/>
      </template>

      <template #content-brands>
        <BrandFilters 
          @update-hotel-name="updateHotelNameFilter"
        />
      </template>

      <template #content-reviews>
        <ReviewQuality @update="updateReviewQualityFilter"/>
      </template>

      <template #content-location>
        <LocationFilters @update="updateLocationFilters"/>
      </template>

      <template #content-amenity-combos>
        <AmenityCombos @update="updateAmenityComboFilters"/>
      </template>
    </UIAccordion>
  </aside>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import Rating from "@/components/MarketPage/Filters/Rating.vue";
import Pricing from "~/components/MarketPage/Filters/Pricing.vue";
import Amenities from "@/components/MarketPage/Filters/Amenities.vue";
import BrandFilters from "@/components/MarketPage/Filters/BrandFilters.vue";
import ReviewQuality from "@/components/MarketPage/Filters/ReviewQuality.vue";
import LocationFilters from "@/components/MarketPage/Filters/LocationFilters.vue";
import AmenityCombos from "@/components/MarketPage/Filters/AmenityCombos.vue";
import UIAccordion from "~/components/UIAccordion.vue";
import { useFilters } from "@/composables/useFilters";
import { useI18n } from 'vue-i18n';
import { useRoute } from "vue-router";

const ACTIVE_FILTER_KEYS = [
  'price_gte', 'price_lte', 'rating_gte', 'amenities', 'hotel_name',
  'free_cancellation', 'board_plans', 'pay_at_property_only', 'pay_now_only',
  'min_rating', 'min_reviews', 'distance_band', 'neighborhoods', 'amenity_combos', 'custom_location'
];

const {
  updateAmenitiesFilter,
  updatePriceFilter,
  updateRatingFilter,
  updateBrandFilters,
  updateFreeCancellationFilter,
  updateBoardPlanFilter,
  updatePaymentTimingFilter,
  updateReviewQualityFilter,
  updateLocationFilters,
  updateAmenityComboFilters,
  clearFilters
} = useFilters();

const { t } = useI18n()
const route = useRoute();

const hasActiveFilters = computed(() => ACTIVE_FILTER_KEYS.some((key) => {
  const value = route.query[key as keyof typeof route.query];
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}));

const onClearFilters = () => {
  clearFilters();
};

// Brand filter handlers
const updateHotelNameFilter = (hotelName: string) => {
  updateBrandFilters([], hotelName);
};

const filterItems = [
  { id: 'pricing', title: t('Pricing') },
  { id: 'rating', title: t('Star class') },
  { id: 'amenities', title: t('Amenities') },
  { id: 'brands', title: t('Hotel Name') },
  { id: 'reviews', title: t('Guest Reviews') },
  { id: 'location', title: t('Location & Distance') },
  { id: 'amenity-combos', title: t('Quick Filters') }
]

</script>

<style lang="scss" scoped src="./filters-style.scss"></style>
